import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Star, Archive, Edit3, RefreshCw, ArrowLeft, X, Check,
  AlertTriangle, Database, Upload, Image as ImageIcon,
} from 'lucide-react';

const STATUS_CHIP = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  inactive: 'bg-gray-100 text-gray-700 border-gray-200',
  archived: 'bg-rose-100 text-rose-700 border-rose-200',
};

const Btn = ({ onClick, children, variant = 'primary', disabled, testid }) => {
  const styles = {
    primary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    danger: 'bg-rose-600 hover:bg-rose-700 text-white',
    ghost: 'border border-gray-200 text-gray-700 hover:bg-gray-50',
    star: 'bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100',
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${styles}`}
    >
      {children}
    </button>
  );
};

const emptyForm = {
  code: '',
  name: '',
  name_ar: '',
  vat_number: '',
  cr_number: '',
  currency: 'SAR',
  timezone: 'Asia/Riyadh',
  locale: 'ar',
  is_default: false,
  logo_url: '',
  brand_color: '',
  max_users: 25,
};

const Input = ({ label, value, onChange, placeholder, disabled, testid }) => (
  <label className="block text-xs">
    <span className="text-gray-700">{label}</span>
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testid}
      className="mt-1 w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-indigo-400 disabled:bg-gray-50"
    />
  </label>
);

const CompanyModal = ({ existing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => existing ? { ...emptyForm, ...existing } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const isEdit = Boolean(existing);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await axiosInstance.put(`/companies/${existing.id}`, form);
        toast.success(`Updated ${form.code}`);
      } else {
        await axiosInstance.post('/companies', form);
        toast.success(`Created ${form.code}`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post('/companies/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setField('logo_url', res.data.logo_url);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  // Backend serves uploads under /api/uploads/... — prefix the absolute API
  // base so the preview <img> resolves even when the React app runs on a
  // different origin.
  const logoPreview = form.logo_url
    ? (form.logo_url.startsWith('http')
        ? form.logo_url
        : `${process.env.REACT_APP_BACKEND_URL || ''}${form.logo_url}`)
    : null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={onClose}
      data-testid="company-modal-overlay"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="company-modal"
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-700" />
            <h3 className="font-bold text-sm">{isEdit ? `Edit ${existing.code}` : 'New Company'}</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900" data-testid="company-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
          <Input label="Code (A-Z, 0-9, _-)" value={form.code} onChange={(v) => setField('code', v)} placeholder="DHR" disabled={isEdit} testid="company-input-code" />
          <Input label="Currency" value={form.currency} onChange={(v) => setField('currency', v)} placeholder="SAR" testid="company-input-currency" />
          <div className="col-span-2"><Input label="Name (EN)" value={form.name} onChange={(v) => setField('name', v)} placeholder="Distinct High Road" testid="company-input-name" /></div>
          <div className="col-span-2"><Input label="Name (AR)" value={form.name_ar} onChange={(v) => setField('name_ar', v)} placeholder="شركة ..." testid="company-input-name_ar" /></div>
          <Input label="VAT Number" value={form.vat_number} onChange={(v) => setField('vat_number', v)} placeholder="3..." testid="company-input-vat_number" />
          <Input label="CR Number" value={form.cr_number} onChange={(v) => setField('cr_number', v)} placeholder="7..." testid="company-input-cr_number" />
          <Input label="Timezone" value={form.timezone} onChange={(v) => setField('timezone', v)} placeholder="Asia/Riyadh" testid="company-input-timezone" />
          <Input label="Locale" value={form.locale} onChange={(v) => setField('locale', v)} placeholder="ar" testid="company-input-locale" />

          {/* Logo upload — appears across sidebar, headers, PDF templates */}
          <div className="col-span-2 mt-1">
            <label className="block text-xs">
              <span className="text-gray-700 font-semibold">Company Logo</span>
              <p className="text-[11px] text-gray-500 mt-0.5">
                PNG / JPG / SVG up to 5MB. Shown in sidebar header and printed documents.
              </p>
            </label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="company-logo-preview">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-semibold hover:bg-indigo-100" data-testid="company-logo-upload-btn">
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingLogo ? 'Uploading…' : (form.logo_url ? 'Replace logo' : 'Upload logo')}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                    data-testid="company-logo-upload-input"
                  />
                </label>
                {form.logo_url && (
                  <button
                    onClick={() => setField('logo_url', '')}
                    className="ml-2 text-[11px] text-rose-700 hover:underline"
                    data-testid="company-logo-clear"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Brand color + seat cap */}
          <label className="block text-xs">
            <span className="text-gray-700">Brand color</span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="color"
                value={form.brand_color || '#4f46e5'}
                onChange={(e) => setField('brand_color', e.target.value)}
                data-testid="company-input-brand_color"
                className="w-10 h-9 rounded border border-gray-200 cursor-pointer"
              />
              <input
                value={form.brand_color || ''}
                onChange={(e) => setField('brand_color', e.target.value)}
                placeholder="#4f46e5"
                className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-indigo-400"
                data-testid="company-input-brand_color-text"
              />
            </div>
          </label>
          <Input
            label="Max users (seat cap)"
            value={String(form.max_users ?? 25)}
            onChange={(v) => setField('max_users', parseInt(v, 10) || 25)}
            placeholder="25"
            testid="company-input-max_users"
          />

          <label className="col-span-2 flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={!!form.is_default}
              onChange={(e) => setField('is_default', e.target.checked)}
              data-testid="company-input-is_default"
            />
            Set as default company
          </label>
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} testid="company-modal-cancel">Cancel</Btn>
          <Btn onClick={save} disabled={saving} testid="company-modal-save">
            <Check className="w-3.5 h-3.5" />{saving ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const Companies = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | existingObj
  const [includeArchived, setIncludeArchived] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, previewRes] = await Promise.all([
        axiosInstance.get('/companies', { params: { include_archived: includeArchived } }),
        axiosInstance.get('/companies/_internal/backfill-preview').catch(() => null),
      ]);
      setItems(listRes.data.items || []);
      setPreview(previewRes?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [listRes, previewRes] = await Promise.all([
          axiosInstance.get('/companies', { params: { include_archived: includeArchived } }),
          axiosInstance.get('/companies/_internal/backfill-preview').catch(() => null),
        ]);
        if (cancelled) return;
        setItems(listRes.data.items || []);
        setPreview(previewRes?.data || null);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Failed to load companies');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeArchived]);

  const promote = async (id) => {
    try {
      await axiosInstance.post(`/companies/${id}/set-default`);
      toast.success('Default company updated');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const archive = async (id, code) => {
    if (!window.confirm(`Archive company ${code}?`)) return;
    try {
      await axiosInstance.delete(`/companies/${id}`);
      toast.success('Archived');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="companies-page">
      <button
        onClick={() => navigate('/settings')}
        className="flex items-center gap-2 text-sm text-indigo-700 hover:underline"
        data-testid="companies-back"
      >
        <ArrowLeft className="w-4 h-4" /> Settings
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-indigo-700" />
            Companies (Tenants)
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            P14-A Foundation — non-breaking. Data isolation activates in P14-C.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600 flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="companies-show-archived"
            />
            Show archived
          </label>
          <Btn variant="ghost" onClick={fetchAll} testid="companies-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Btn>
          <Btn onClick={() => setEditing('new')} testid="companies-new">
            <Plus className="w-3.5 h-3.5" /> New
          </Btn>
        </div>
      </div>

      {/* Phase notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs flex items-start gap-3" data-testid="phase-notice">
        <AlertTriangle className="w-4 h-4 text-amber-700 flex-none mt-0.5" />
        <div className="text-amber-900">
          <div className="font-semibold">Phase A — Foundation only</div>
          <div className="mt-1">
            Creating companies here does not yet isolate data. Existing operations
            stay attached to the default tenant <code className="font-mono px-1 bg-amber-100 rounded">cmp_001</code>.
            Migration (Phase B) will backfill <code>company_id</code> on every existing document; enforcement ships in Phase C.
          </div>
        </div>
      </div>

      {/* Backfill preview */}
      {preview && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="backfill-preview">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-indigo-700" />
              <h3 className="font-bold text-sm">Backfill Preview</h3>
            </div>
            <div className="text-xs text-gray-600">
              total docs missing <code>company_id</code>: <span className="font-bold text-rose-700" data-testid="preview-missing-total">{preview.total_missing}</span>
            </div>
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-3">Collection</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3">Missing</th>
                </tr>
              </thead>
              <tbody>
                {(preview.rows || []).map((r) => (
                  <tr key={r.collection} className="border-b last:border-0">
                    <td className="py-1.5 px-3 font-mono">{r.collection}</td>
                    <td className="py-1.5 px-3 text-right">{r.total ?? '—'}</td>
                    <td className={`py-1.5 px-3 text-right ${r.missing > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700'}`}>
                      {r.missing ?? r.error ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-bold text-sm">Tenants ({items.length})</h3>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No companies yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2 px-4">Code</th>
                  <th className="text-left py-2 px-4">Name</th>
                  <th className="text-left py-2 px-4">VAT</th>
                  <th className="text-left py-2 px-4">Currency</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 px-4 font-mono font-semibold">
                      <span className="flex items-center gap-1.5">
                        {c.code}
                        {c.is_default && <Star className="w-3 h-3 text-amber-500 fill-amber-400" />}
                      </span>
                    </td>
                    <td className="py-2 px-4">
                      <div className="text-gray-900">{c.name}</div>
                      {c.name_ar && <div className="text-gray-500 text-[11px]">{c.name_ar}</div>}
                    </td>
                    <td className="py-2 px-4 font-mono">{c.vat_number || '—'}</td>
                    <td className="py-2 px-4">{c.currency}</td>
                    <td className="py-2 px-4">
                      <span className={`px-2 py-0.5 rounded border text-[10px] uppercase ${STATUS_CHIP[c.status] || ''}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-right">
                      <div className="flex justify-end gap-1.5">
                        {!c.is_default && c.status === 'active' && (
                          <Btn variant="star" onClick={() => promote(c.id)} testid={`promote-${c.id}`}>
                            <Star className="w-3 h-3" /> Default
                          </Btn>
                        )}
                        <Btn variant="ghost" onClick={() => setEditing(c)} testid={`edit-${c.id}`}>
                          <Edit3 className="w-3 h-3" /> Edit
                        </Btn>
                        {c.status !== 'archived' && !c.is_default && c.id !== 'cmp_001' && (
                          <Btn variant="danger" onClick={() => archive(c.id, c.code)} testid={`archive-${c.id}`}>
                            <Archive className="w-3 h-3" />
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <CompanyModal
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

export default Companies;
